import { Link } from "@tanstack/react-router";
import { OFFER, STUDIO_ADDONS } from "@/lib/site-content";
import { TalkToJonButton } from "@/components/talk-to-jon";
import { Check } from "@/components/icons";

/**
 * The one public offer, plus the studio work that sits on top of it.
 * Bookings happen on the call, so both cards end in "Talk to Jon".
 */
export function PricingPlans({ heading = "One price. One date." }: { heading?: string }) {
  return (
    <section
      id="pricing"
      aria-labelledby="pricing-h"
      className="relative mx-auto max-w-[1400px] border-t border-border/60 px-6 py-20 md:px-10 md:py-32"
    >
      <div className="max-w-[46ch]">
        <h2 id="pricing-h" className="fluid-h2 font-display tracking-[-0.035em]">
          {heading}
        </h2>
        <p className="mt-6 text-pretty text-lg leading-snug text-muted-foreground md:text-xl">
          {OFFER.price} fixed takes a website or web app from idea to live product in {OFFER.days}{" "}
          days. Everything else the studio does sits on top.
        </p>
      </div>

      <div className="mt-12 grid gap-5 lg:grid-cols-12">
        {/* The offer */}
        <article className="flex flex-col rounded-[2rem] bg-ink p-8 text-paper ring-1 ring-ink md:p-11 lg:col-span-7">
          <p className="text-sm text-paper/60">
            {OFFER.days} days · fixed price · for teams that need to prove it fast
          </p>
          <h3 className="mt-4 font-display text-2xl tracking-[-0.03em] leading-[1.05] md:text-4xl">
            {OFFER.name} in {OFFER.days} days
          </h3>
          <p className="mt-4 max-w-[46ch] text-pretty leading-snug text-paper/70">
            Product strategy, UI/UX and the whole build by one team of full-stack designers. One
            launch date, and you own everything when it ships.
          </p>

          <p className="mt-10 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span
              className="font-display leading-none tracking-[-0.05em] tabular-nums"
              style={{ fontSize: "clamp(3rem, 6vw, 5rem)" }}
            >
              {OFFER.price}
            </span>
            <span className="text-base text-paper/60">{OFFER.cadence}</span>
          </p>
          <p className="mt-2 text-sm text-paper/60">Scope locked on day 1. Live on day {OFFER.days}.</p>

          <div className="mt-8">
            <TalkToJonButton variant="light" />
          </div>

          <ul className="mt-10 grid gap-3 border-t border-paper/15 pt-8 text-[15px] sm:grid-cols-2 sm:gap-x-8">
            {OFFER.includes.map((inc) => (
              <li key={inc} className="flex items-start gap-3">
                <Check aria-hidden className="mt-[3px] h-4 w-4 shrink-0 text-electric-bright" />
                <span className="text-paper/85">{inc}</span>
              </li>
            ))}
          </ul>

          <div className="mt-auto pt-10">
            <p className="text-sm text-paper/60">Built for</p>
            <ul className="mt-3 flex flex-wrap gap-2">
              {OFFER.builtFor.map((who) => (
                <li
                  key={who}
                  className="rounded-full bg-paper/10 px-3.5 py-1.5 text-sm text-paper/85 ring-1 ring-paper/10"
                >
                  {who}
                </li>
              ))}
            </ul>
          </div>
        </article>

        {/* The studio around it */}
        <aside
          aria-labelledby="addons-h"
          className="flex flex-col rounded-[2rem] bg-secondary/70 p-8 ring-1 ring-foreground/[0.05] md:p-11 lg:col-span-5"
        >
          <p className="text-sm text-muted-foreground">Creative studio · scoped on the call</p>
          <h3
            id="addons-h"
            className="mt-4 font-display text-2xl tracking-[-0.03em] leading-[1.05] md:text-3xl"
          >
            The studio around it
          </h3>
          <p className="mt-4 max-w-[36ch] text-pretty leading-snug text-muted-foreground">
            Add to the {OFFER.days} days, or book after launch. Same designers, same standard.
          </p>

          <ul className="mt-8 divide-y divide-foreground/[0.07] border-t border-foreground/[0.07]">
            {STUDIO_ADDONS.map((a) => (
              <li key={a.id} className="py-4">
                <p className="font-display text-lg tracking-[-0.02em]">{a.name}</p>
                <p className="mt-1 text-pretty text-[15px] leading-snug text-muted-foreground">
                  {a.blurb}
                </p>
              </li>
            ))}
          </ul>

          <div className="mt-8">
            <TalkToJonButton variant="dark" />
          </div>
        </aside>
      </div>

      <p className="mt-8 max-w-[70ch] text-[15px] leading-relaxed text-muted-foreground">
        Something smaller? We also{" "}
        <Link
          to="/services/vibe-coding-rescue"
          className="text-foreground underline decoration-border underline-offset-4 hover:decoration-foreground"
        >
          rescue stalled builds
        </Link>
        ,{" "}
        <Link
          to="/services/lovable-cloud-migration"
          className="text-foreground underline decoration-border underline-offset-4 hover:decoration-foreground"
        >
          migrate apps off Lovable Cloud
        </Link>{" "}
        and{" "}
        <Link
          to="/services/app-development-consultant"
          className="text-foreground underline decoration-border underline-offset-4 hover:decoration-foreground"
        >
          advise
        </Link>
        . Bring it to the call.
      </p>
    </section>
  );
}
